import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  styled,
} from '@mui/material';
import { Linkedin, Twitter } from 'lucide-react';
import { Link } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import bhagwati from '../../assets/images/BPL_Logo.png';

// Custom styled components
const FooterLink = styled(Link)(({ theme }) => ({
  color: '#4b5563',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '0.9rem',
  marginBottom: '8px',
  '&:hover': {
    color: '#000',
    textDecoration: 'none',
  },
}));

const FooterHeading = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: '1.5rem',
  color: '#000',
}));

const NewChip = styled(Chip)({
  backgroundColor: '#EEF2FF',
  color: '#4F46E5',
  height: '20px',
  fontSize: '0.75rem',
  marginLeft: '8px',
});

const HiringChip = styled(Chip)({
  backgroundColor: '#818CF8',
  color: '#fff',
  height: '20px',
  fontSize: '0.75rem',
  marginLeft: '8px',
});

export default function Footer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div>
      <Box
        component="footer"
        sx={{
          py: { xs: 6, md: 8 },
          backgroundColor: '#fff',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            {/* Logo and Tagline Section */}
            <Grid item xs={12} md={3}>
              <Box sx={{ mb: 3 }}>
                <img 
                  src={bhagwati || "/placeholder.svg"} 
                  alt="Bhagwati Logo"
                  style={{
                    height: '100px',
                    width: 'auto',
                    objectFit: 'contain',
                    marginRight: '16px'
                  }}
                />
              </Box>
            </Grid>

            

            {/* Services Column */}
            <Grid item xs={12} sm={6} md={2}>
              <FooterHeading>About</FooterHeading>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FooterLink to="/about">Company Overview</FooterLink>
                <FooterLink to="/vision-mission">Our Vision and Mission</FooterLink>
                <FooterLink to="/factories">Our Presence</FooterLink>
                <FooterLink to="/history">Timeline and Milestones</FooterLink>
                <FooterLink to="/team">Our Leadership</FooterLink>
                <FooterLink to="/partner">Our Partners</FooterLink>
              </Box>
            </Grid>

             {/* Services Column */}
             <Grid item xs={12} sm={6} md={2}>
              <FooterHeading>Services</FooterHeading>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FooterLink to="/manufacturing">Our Divisions</FooterLink>
                {/* <FooterLink to="/smt-manufacturing">Smt Manufacturing</FooterLink> */}
                <FooterLink to="/mobile">Mobile Phones</FooterLink>
                <FooterLink to="/tablet">Tablets</FooterLink>
                {/* <FooterLink to="/">PBC</FooterLink> */}
                <FooterLink to="/led-manufacturing">LED TV Manufacturing</FooterLink>
              </Box>
            </Grid>

            {/* Company Column */}
            <Grid item xs={12} sm={6} md={2}>
              <FooterHeading>Company</FooterHeading>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FooterLink to="/partners">Partnership Directory</FooterLink>
                <FooterLink to="/responsibility">Responsibility</FooterLink>
                {/* <FooterLink to="/">Our Employees</FooterLink> */}
                <FooterLink to="/careers">Careers</FooterLink>
                {/* <FooterLink to="/csr">CSR</FooterLink>
                <FooterLink to="/e-waste">E-Waste Management</FooterLink> */}
              </Box>
            </Grid>

            {/* Resources Column */}
            <Grid item xs={12} sm={6} md={2}>
              <FooterHeading>Resources</FooterHeading>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FooterLink to="/contact">Contact Us</FooterLink>
                <FooterLink to="/blogs">Blogs</FooterLink>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <div className="w-full bg-black text-gray-300 py-3 px-4 mt-0">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center text-sm">
          <div className="flex justify-start items-center my-2 md:my-0 md:text-left">
            <span>{new Date().getFullYear()} © BPL</span>
            <span className="mx-2">|</span>
            <span>Bhagwati Products Limited</span>   
          </div>

          <div className="flex items-center space-x-4">
            <a 
              href="https://x.com/BhagwatiPL" 
              className="text-gray-300 hover:text-white transition-colors"
              aria-label="Twitter"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg 
                viewBox="0 0 24 24" 
                className="w-6 h-6 cursor-pointer"
                fill="currentColor"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a 
              href="https://www.linkedin.com/company/bhagwatiproductslimited/posts/?feedView=all" 
              className="text-gray-300 hover:text-white transition-colors"
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Linkedin size={20} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

